import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
console.log('API_URL configured:', API_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
// Endpoints where a 401 means "wrong credentials for this action", NOT an expired
// session — these must not trigger an automatic redirect to the login screen.
const NON_SESSION_401_PATHS = ['/auth/login', '/auth/verify-password'];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isSessionEndpoint = !NON_SESSION_401_PATHS.some((path) => url.includes(path));
    if (error.response?.status === 401 && isSessionEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// ============== Auth API ==============
export const authApi = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  me: () => api.get('/api/auth/me'),
  updateMe: (data) => api.put('/api/auth/me', data),
  permissions: () => api.get('/api/auth/permissions'),
};

// ============== Users API ==============
export const usersApi = {
  list: (params) => api.get('/api/users', { params }),
  get: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
  permanentDelete: (id) => api.delete(`/api/users/${id}/permanent`),
};

// ============== Profiles API ==============
export const profilesApi = {
  list: (params) => api.get('/api/profiles', { params }),
  get: (id) => api.get(`/api/profiles/${id}`),
  create: (data) => api.post('/api/profiles', data),
  update: (id, data) => api.put(`/api/profiles/${id}`, data),
  delete: (id) => api.delete(`/api/profiles/${id}`),
};

// ============== Permissions API ==============
export const permissionsApi = {
  list: () => api.get('/api/permissions'),
  grouped: () => api.get('/api/permissions/grouped'),
};

// ============== Documents API ==============
export const documentsApi = {
  list: (params) => api.get('/api/documents', { params }),
  get: (id) => api.get(`/api/documents/${id}`),
  create: (data) => api.post('/api/documents', data),
  update: (id, data) => api.put(`/api/documents/${id}`, data),
  delete: (id) => api.delete(`/api/documents/${id}`),
  submitForApproval: (id, approverId) => api.post(`/api/documents/${id}/submit-for-approval`, { approver_id: approverId }),
  approve: (id) => api.post(`/api/documents/${id}/approve`),
  obsolete: (id, reason) => api.post(`/api/documents/${id}/obsolete?reason=${encodeURIComponent(reason)}`),
  reactivate: (id, data, adminToken) => api.post(`/api/documents/${id}/reactivate`, data, {
    headers: { Authorization: `Bearer ${adminToken}` },
  }),
  stats: () => api.get('/api/documents/stats'),
  // File operations
  uploadFile: (id, file, { revisionNumber, revisionDate, oldVersions = [] } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (revisionNumber !== undefined && revisionNumber !== '') formData.append('revision_number', revisionNumber);
    if (revisionDate) formData.append('revision_date', revisionDate);
    oldVersions.forEach((old) => {
      formData.append('old_files', old.file);
      formData.append('old_revision_numbers', old.number);
      formData.append('old_revision_dates', old.date);
    });
    return api.post(`/api/documents/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadFile: (id) => api.get(`/api/documents/${id}/download`, { responseType: 'blob' }),
  viewFile: (id) => `${API_URL}/api/documents/${id}/view`,
  deleteFile: (id) => api.delete(`/api/documents/${id}/file`),
  // Immutable versions and internal rich text editing
  listVersions: (id) => api.get(`/api/documents/${id}/versions`),
  downloadVersion: (documentId, versionId) => api.get(`/api/documents/${documentId}/versions/${versionId}/download`, { responseType: 'blob' }),
  saveEditorVersion: (id, file, changeNotes) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/documents/${id}/versions/editor${changeNotes ? `?change_notes=${encodeURIComponent(changeNotes)}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Structured controlled documents
  listTemplates: () => api.get('/api/documents/templates'),
  createTemplate: (data) => api.post('/api/documents/templates', data),
  uploadTemplateFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/documents/templates/${id}/file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getContent: (id) => api.get(`/api/documents/${id}/content`),
  saveContent: (id, data) => api.post(`/api/documents/${id}/content`, data),
  listContentVersions: (id) => api.get(`/api/documents/${id}/content/versions`),
  previewContent: (id) => api.get(`/api/documents/${id}/content/preview`, { responseType: 'blob' }),
  downloadControlledPdf: (id) => api.get(`/api/documents/${id}/controlled-pdf`, { responseType: 'blob' }),
  importLegacy: (files, consent = true) => {
    const formData = new FormData();
    formData.append('consent', String(consent));
    files.forEach(file => formData.append('files', file));
    return api.post('/api/documents/imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  listImports: () => api.get('/api/documents/imports'),
  downloadImportOriginal: (batchId, itemId) => api.get(`/api/documents/imports/${batchId}/items/${itemId}/original`, { responseType: 'blob' }),
  // Revisions
  listRevisions: (params) => api.get('/api/documents/revisions/all', { params }),
  createRevision: (data) => api.post('/api/documents/revisions', data),
  completeRevision: (id, description) => api.put(`/api/documents/revisions/${id}/complete${description ? `?changes_description=${encodeURIComponent(description)}` : ''}`),
  // History
  listHistory: (params) => api.get('/api/documents/history/all', { params }),
  listCodeHistory: (documentId) => api.get(`/api/documents/${documentId}/code-history`),
  downloadHistoryFile: (historyId) => api.get(`/api/documents/history/${historyId}/download`, { responseType: 'blob' }),
  viewHistoryFile: (historyId) => `${API_URL}/api/documents/history/${historyId}/view`,
};

// ============== Master List API ==============
export const masterListApi = {
  list: (params) => api.get('/api/master-list', { params }),
  get: (id) => api.get(`/api/master-list/${id}`),
  update: (id, data) => api.put(`/api/master-list/${id}`, data),
  stats: () => api.get('/api/master-list/stats'),
};

// ============== External Documents API ==============
export const externalDocumentsApi = {
  // Groups
  listGroups: () => api.get('/api/external-documents/groups'),
  createGroup: (data) => api.post('/api/external-documents/groups', data),
  updateGroup: (id, data) => api.put(`/api/external-documents/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/api/external-documents/groups/${id}`),
  // Documents
  list: (params) => api.get('/api/external-documents/documents', { params }),
  get: (id) => api.get(`/api/external-documents/documents/${id}`),
  create: (data) => api.post('/api/external-documents/documents', data),
  update: (id, data) => api.put(`/api/external-documents/documents/${id}`, data),
  delete: (id) => api.delete(`/api/external-documents/documents/${id}`),
  stats: () => api.get('/api/external-documents/documents/stats'),
  // File operations
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/external-documents/documents/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadFile: (id) => api.get(`/api/external-documents/documents/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/api/external-documents/documents/${id}/file`),
  // Version history
  getVersions: (id) => api.get(`/api/external-documents/documents/${id}/versions`),
  downloadVersion: (versionId) => api.get(`/api/external-documents/documents/versions/${versionId}/download`, { responseType: 'blob' }),
};

// ============== Client Documents API (aba Documentos Clientes) ==============
export const clientDocumentsApi = {
  // Groups
  listGroups: () => api.get('/api/client-documents/groups'),
  createGroup: (data) => api.post('/api/client-documents/groups', data),
  updateGroup: (id, data) => api.put(`/api/client-documents/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/api/client-documents/groups/${id}`),
  // Documents
  list: (params) => api.get('/api/client-documents/documents', { params }),
  get: (id) => api.get(`/api/client-documents/documents/${id}`),
  create: (data) => api.post('/api/client-documents/documents', data),
  update: (id, data) => api.put(`/api/client-documents/documents/${id}`, data),
  delete: (id) => api.delete(`/api/client-documents/documents/${id}`),
  stats: () => api.get('/api/client-documents/documents/stats'),
  // File operations
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/client-documents/documents/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadFile: (id) => api.get(`/api/client-documents/documents/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/api/client-documents/documents/${id}/file`),
};


// ============== Change Requests API ==============
export const changeRequestsApi = {
  list: (params) => api.get('/api/change-requests', { params }),
  get: (id) => api.get(`/api/change-requests/${id}`),
  create: (data) => api.post('/api/change-requests', data),
  update: (id, data) => api.put(`/api/change-requests/${id}`, data),
  delete: (id) => api.delete(`/api/change-requests/${id}`),
  approve: (id, comments) => api.post(`/api/change-requests/${id}/approve${comments ? `?comments=${encodeURIComponent(comments)}` : ''}`),
  reject: (id, reason) => api.post(`/api/change-requests/${id}/reject?reason=${encodeURIComponent(reason)}`),
  stats: () => api.get('/api/change-requests/stats'),
};

// ============== Non-Conformities API ==============
export const nonConformitiesApi = {
  list: (params) => api.get('/api/non-conformities/', { params }),
  get: (id) => api.get(`/api/non-conformities/${id}`),
  create: (data) => api.post('/api/non-conformities/', data),
  update: (id, data) => api.put(`/api/non-conformities/${id}`, data),
  delete: (id) => api.delete(`/api/non-conformities/${id}`),
  stats: () => api.get('/api/non-conformities/stats'),
  // Actions
  listActions: (ncId) => api.get(`/api/non-conformities/${ncId}/actions`),
  createAction: (ncId, data) => api.post(`/api/non-conformities/${ncId}/actions`, data),
  updateAction: (actionId, status, notes) => api.put(`/api/non-conformities/actions/${actionId}?status_update=${status}${notes ? `&completion_notes=${encodeURIComponent(notes)}` : ''}`),
};

// ============== SACPM API ==============
export const sacpmApi = {
  list: (params) => api.get('/api/sacpm/', { params }),
  get: (id) => api.get(`/api/sacpm/${id}`),
  create: (data) => api.post('/api/sacpm/', data),
  update: (id, data) => api.put(`/api/sacpm/${id}`, data),
  delete: (id) => api.delete(`/api/sacpm/${id}`),
  stats: () => api.get('/api/sacpm/stats'),
  // Actions
  listActions: (sacpmId) => api.get(`/api/sacpm/${sacpmId}/actions`),
  createAction: (sacpmId, data) => api.post(`/api/sacpm/${sacpmId}/actions`, data),
  updateAction: (actionId, data) => api.put(`/api/sacpm/actions/${actionId}`, data),
  deleteAction: (actionId) => api.delete(`/api/sacpm/actions/${actionId}`),
  // Attachments
  uploadAttachment: (sacpmId, file, kind = 'file') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    return api.post(`/api/sacpm/${sacpmId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  listAttachments: (sacpmId) => api.get(`/api/sacpm/${sacpmId}/attachments`),
  downloadAttachment: (attachmentId) => api.get(`/api/sacpm/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId) => api.delete(`/api/sacpm/attachments/${attachmentId}`),
  // Closure
  close: (sacpmId, { photo, effectiveness, verification_result }) => {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('effectiveness', effectiveness);
    if (verification_result) formData.append('verification_result', verification_result);
    return api.post(`/api/sacpm/${sacpmId}/close`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // PDF
  getPdf: (id) => api.get(`/api/sacpm/${id}/pdf`, { responseType: 'blob' }),
};

// ============== Audits API ==============
export const auditsApi = {
  list: (params) => api.get('/api/audits/', { params }),
  get: (id) => api.get(`/api/audits/${id}`),
  create: (data) => api.post('/api/audits/', data),
  update: (id, data) => api.put(`/api/audits/${id}`, data),
  delete: (id) => api.delete(`/api/audits/${id}`),
  stats: () => api.get('/api/audits/stats'),
  // Findings
  listFindings: (auditId) => api.get(`/api/audits/${auditId}/findings`),
  createFinding: (auditId, data) => api.post(`/api/audits/${auditId}/findings`, data),
  updateFinding: (findingId, ncId, sacpmId) => api.put(`/api/audits/findings/${findingId}`, null, { params: { nc_id: ncId, sacpm_id: sacpmId } }),
  // Checklist (F3-06)
  listChecklist: (auditId) => api.get(`/api/audits/${auditId}/checklist`),
  addChecklistItem: (auditId, data) => api.post(`/api/audits/${auditId}/checklist`, data),
  updateChecklistItem: (itemId, data) => api.put(`/api/audits/checklist/${itemId}`, data),
  deleteChecklistItem: (itemId) => api.delete(`/api/audits/checklist/${itemId}`),
  // Checklist sectors (Agenda N setores por item)
  listItemSectors: (itemId) => api.get(`/api/audits/checklist/${itemId}/sectors`),
  addItemSector: (itemId, data) => api.post(`/api/audits/checklist/${itemId}/sectors`, data),
  updateItemSector: (linkId, data) => api.put(`/api/audits/checklist/sectors/${linkId}`, data),
  deleteItemSector: (linkId) => api.delete(`/api/audits/checklist/sectors/${linkId}`),
  updateReport: (auditId, params) => api.put(`/api/audits/${auditId}/report`, null, { params }),
  // Audit report (Relatório de Auditoria)
  getReport: (auditId) => api.get(`/api/audits/${auditId}/report`),
  saveReport: (auditId, data) => api.put(`/api/audits/${auditId}/report/data`, data),
  exportReportPdf: (auditId) => api.get(`/api/audits/${auditId}/report/pdf`, { responseType: 'blob' }),
  // Agenda Auditoria Interna PDF
  exportAgendaPdf: (auditId) => api.get(`/api/audits/${auditId}/agenda/pdf`, { responseType: 'blob' }),
  // Attached documents (external audits)
  listDocuments: (auditId) => api.get(`/api/audits/${auditId}/documents`),
  uploadDocument: (auditId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/audits/${auditId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: (documentId) => api.get(`/api/audits/documents/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (documentId) => api.delete(`/api/audits/documents/${documentId}`),
};

// ============== Audit Items API (Cadastro de Itens de Auditoria) ==============
export const auditItemsApi = {
  list: (params) => api.get('/api/audit-items', { params }),
  get: (id) => api.get(`/api/audit-items/${id}`),
  create: (data) => api.post('/api/audit-items', data),
  update: (id, data) => api.put(`/api/audit-items/${id}`, data),
  delete: (id) => api.delete(`/api/audit-items/${id}`),
  reactivate: (id) => api.patch(`/api/audit-items/${id}/reactivate`),
};

// ============== Certificates API (Cadastro de Certificações) ==============
export const certificatesApi = {
  list: (params) => api.get('/api/certificates', { params }),
  listActive: (params) => api.get('/api/certificates/active', { params }),
  get: (id) => api.get(`/api/certificates/${id}`),
  create: (data) => api.post('/api/certificates', data),
  update: (id, data) => api.put(`/api/certificates/${id}`, data),
  delete: (id) => api.delete(`/api/certificates/${id}`),
  reactivate: (id) => api.patch(`/api/certificates/${id}/reactivate`),
};

// ============== Auditors API (Cadastro de Auditores) ==============
export const auditorsApi = {
  list: (params) => api.get('/api/auditors', { params }),
  listActive: () => api.get('/api/auditors/active'),
  report: (params) => api.get('/api/auditors/report', { params }),
  get: (id) => api.get(`/api/auditors/${id}`),
  create: (data) => api.post('/api/auditors', data),
  update: (id, data) => api.put(`/api/auditors/${id}`, data),
  delete: (id) => api.delete(`/api/auditors/${id}`),
  // Attachments (documentação do auditor)
  listAttachments: (auditorId) => api.get(`/api/auditors/${auditorId}/attachments`),
  uploadAttachment: (auditorId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/auditors/${auditorId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadAttachment: (attachmentId) => api.get(`/api/auditors/attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId) => api.delete(`/api/auditors/attachments/${attachmentId}`),
};

// ============== Indicators API ==============
export const indicatorsApi = {
  list: (params) => api.get('/api/indicators/', { params }),
  get: (id) => api.get(`/api/indicators/${id}`),
  create: (data) => api.post('/api/indicators/', data),
  update: (id, data) => api.put(`/api/indicators/${id}`, data),
  delete: (id) => api.delete(`/api/indicators/${id}`),
  stats: () => api.get('/api/indicators/stats'),
  // Measurements
  listMeasurements: (indicatorId, params) => api.get(`/api/indicators/${indicatorId}/measurements`, { params }),
  createMeasurement: (indicatorId, data) => api.post(`/api/indicators/${indicatorId}/measurements`, data),
  // Goals
  listGoals: (indicatorId, params) => api.get(`/api/indicators/${indicatorId}/goals`, { params }),
  createGoal: (indicatorId, data) => api.post(`/api/indicators/${indicatorId}/goals`, data),
};

// ============== Indicator Plans API (F-021) ==============
export const indicatorPlansApi = {
  list: (params) => api.get('/api/indicator-plans/', { params }),
  create: (data) => api.post('/api/indicator-plans/', data),
  update: (id, data) => api.put(`/api/indicator-plans/${id}`, data),
  delete: (id) => api.delete(`/api/indicator-plans/${id}`),
  exportXlsx: (params) => api.get('/api/indicator-plans/export', { params, responseType: 'blob' }),
  importXlsx: (year, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/api/indicator-plans/import', form, { params: { year } });
  },
};

// ============== Risks API ==============
export const risksApi = {
  list: (params) => api.get('/api/risks/', { params }),
  get: (id) => api.get(`/api/risks/${id}`),
  create: (data) => api.post('/api/risks/', data),
  update: (id, data) => api.put(`/api/risks/${id}`, data),
  delete: (id) => api.delete(`/api/risks/${id}`),
  stats: () => api.get('/api/risks/stats'),
  matrix: () => api.get('/api/risks/matrix'),
  // Actions
  listActions: (riskId) => api.get(`/api/risks/${riskId}/actions`),
  createAction: (riskId, data) => api.post(`/api/risks/${riskId}/actions`, data),
  // Reviews
  listReviews: (riskId) => api.get(`/api/risks/${riskId}/reviews`),
  createReview: (riskId, data) => api.post(`/api/risks/${riskId}/reviews`, data),
};

// ============== SWOT API ==============
export const swotApi = {
  list: (params) => api.get('/api/swot/', { params }),
  get: (id) => api.get(`/api/swot/${id}`),
  create: (data) => api.post('/api/swot/', data),
  update: (id, data) => api.put(`/api/swot/${id}`, data),
  delete: (id) => api.delete(`/api/swot/${id}`),
  stats: () => api.get('/api/swot/stats'),
  // Items
  listItems: (analysisId, params) => api.get(`/api/swot/${analysisId}/items`, { params }),
  createItem: (analysisId, data) => api.post(`/api/swot/${analysisId}/items`, data),
  updateItem: (itemId, data) => api.put(`/api/swot/items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/api/swot/items/${itemId}`),
  // Actions
  listItemActions: (itemId) => api.get(`/api/swot/items/${itemId}/actions`),
  createItemAction: (itemId, data) => api.post(`/api/swot/items/${itemId}/actions`, data),
};

// ============== Ata de Reunião API (F-022) ==============
export const meetingMinutesApi = {
  list: (params) => api.get('/api/meeting-minutes/', { params }),
  get: (id) => api.get(`/api/meeting-minutes/${id}`),
  create: (data) => api.post('/api/meeting-minutes/', data),
  update: (id, data) => api.put(`/api/meeting-minutes/${id}`, data),
  delete: (id) => api.delete(`/api/meeting-minutes/${id}`),
  requestSignature: (id, idx) => api.post(`/api/meeting-minutes/${id}/participants/${idx}/request-signature`),
  getPdf: (id) => api.get(`/api/meeting-minutes/${id}/pdf`, { responseType: 'blob' }),
};

// ============== Alta Direção API (F-017 - Análise Crítica pela Direção) ==============
export const topManagementApi = {
  list: (params) => api.get('/api/top-management/', { params }),
  get: (id) => api.get(`/api/top-management/${id}`),
  create: (data) => api.post('/api/top-management/', data),
  update: (id, data) => api.put(`/api/top-management/${id}`, data),
  delete: (id) => api.delete(`/api/top-management/${id}`),
  requestSignature: (id, idx) => api.post(`/api/top-management/${id}/participants/${idx}/request-signature`),
  getPdf: (id) => api.get(`/api/top-management/${id}/pdf`, { responseType: 'blob' }),
};

// ============== Ata Items API (Cadastro de Itens de ATA — Pauta/Ações) ==============
export const ataItemsApi = {
  list: (params) => api.get('/api/ata-items', { params }),
  get: (id) => api.get(`/api/ata-items/${id}`),
  create: (data) => api.post('/api/ata-items', data),
  update: (id, data) => api.put(`/api/ata-items/${id}`, data),
  delete: (id) => api.delete(`/api/ata-items/${id}`),
  reactivate: (id) => api.patch(`/api/ata-items/${id}/reactivate`),
};

// ============== Assinaturas públicas (link enviado por e-mail) ==============
export const signaturesApi = {
  get: (module, token) => api.get(`/api/signatures/${module}/${token}`),
  submit: (module, token, data) => api.post(`/api/signatures/${module}/${token}`, data),
};

// Helper to filter out empty params
const cleanParams = (params) => {
  if (!params) return params;
  const cleaned = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  });
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

// ============== Calibrations API ==============
export const calibrationsApi = {
  // Instruments
  listInstruments: (params) => api.get('/api/calibrations/instruments/', { params: cleanParams(params) }),
  getInstrument: (id) => api.get(`/api/calibrations/instruments/${id}`),
  createInstrument: (data) => api.post('/api/calibrations/instruments/', data),
  updateInstrument: (id, data) => api.put(`/api/calibrations/instruments/${id}`, data),
  deleteInstrument: (id) => api.delete(`/api/calibrations/instruments/${id}`),
  instrumentStats: () => api.get('/api/calibrations/instruments/stats'),
  // Calibrations
  listCalibrations: (instrumentId) => api.get(`/api/calibrations/instruments/${instrumentId}/history`),
  createCalibration: (instrumentId, data) => api.post(`/api/calibrations/instruments/${instrumentId}/calibrate`, data),
  downloadCalibrationCertificate: (calibrationId) => api.get(`/api/calibrations/${calibrationId}/certificate/download`, { responseType: 'blob' }),
  downloadAllCertificates: (instrumentId) => api.get(`/api/calibrations/instruments/${instrumentId}/certificates/download-all`, { responseType: 'blob' }),
  uploadCalibrationCertificate: (calibrationId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/calibrations/${calibrationId}/certificate/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCalibrationCertificate: (calibrationId) => api.delete(`/api/calibrations/${calibrationId}/certificate`),
};

// ============== Suppliers API ==============
export const suppliersApi = {
  list: (params) => api.get('/api/suppliers/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/suppliers/${id}`),
  create: (data) => api.post('/api/suppliers/', data),
  update: (id, data) => api.put(`/api/suppliers/${id}`, data),
  delete: (id) => api.delete(`/api/suppliers/${id}`),
  stats: () => api.get('/api/suppliers/stats'),
  // Evaluations
  listEvaluations: (supplierId) => api.get(`/api/suppliers/${supplierId}/evaluations`),
  createEvaluation: (supplierId, data) => api.post(`/api/suppliers/${supplierId}/evaluate`, data),
  // Documents (F3-11)
  listDocuments: (supplierId) => api.get(`/api/suppliers/${supplierId}/documents`),
  createDocument: (supplierId, data) => api.post(`/api/suppliers/${supplierId}/documents`, data),
  updateDocument: (docId, data) => api.put(`/api/suppliers/documents/${docId}`, data),
  deleteDocument: (docId) => api.delete(`/api/suppliers/documents/${docId}`),
};

// ============== Regulatório: Controle de Pragas API ==============
export const pestControlApi = {
  list: (params) => api.get('/api/regulatory-control/pest-control/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/regulatory-control/pest-control/${id}`),
  create: (data) => api.post('/api/regulatory-control/pest-control/', data),
  update: (id, data) => api.put(`/api/regulatory-control/pest-control/${id}`, data),
  delete: (id) => api.delete(`/api/regulatory-control/pest-control/${id}`),
  stats: (params) => api.get('/api/regulatory-control/pest-control/stats', { params: cleanParams(params) }),
};

// ============== Regulatório: Lavagem de Caixa D'Água API ==============
export const waterTankCleaningApi = {
  list: (params) => api.get('/api/regulatory-control/water-tank-cleaning/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/regulatory-control/water-tank-cleaning/${id}`),
  create: (data) => api.post('/api/regulatory-control/water-tank-cleaning/', data),
  update: (id, data) => api.put(`/api/regulatory-control/water-tank-cleaning/${id}`, data),
  delete: (id) => api.delete(`/api/regulatory-control/water-tank-cleaning/${id}`),
  stats: () => api.get('/api/regulatory-control/water-tank-cleaning/stats'),
};

// ============== Controle (grupos + formulário padrão) API ==============
export const controlApi = {
  listGroups: () => api.get('/api/control/groups'),
  createGroup: (data) => api.post('/api/control/groups', data),
  updateGroup: (id, data) => api.put(`/api/control/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/api/control/groups/${id}`),

  listRecords: (params) => api.get('/api/control/records', { params: cleanParams(params) }),
  createRecord: (data) => api.post('/api/control/records', data),
  updateRecord: (id, data) => api.put(`/api/control/records/${id}`, data),
  deleteRecord: (id) => api.delete(`/api/control/records/${id}`),
  stats: (params) => api.get('/api/control/records/stats', { params: cleanParams(params) }),
  getRecordPdf: (id) => api.get(`/api/control/records/${id}/pdf`, { responseType: 'blob' }),

  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/control/records/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadFile: (id) => api.get(`/api/control/records/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/api/control/records/${id}/file`),
};

// ============== Qualificações (Qualificação / Qualificação Térmica) API ==============
export const qualificationsApi = {
  list: (params) => api.get('/api/qualifications/records', { params: cleanParams(params) }),
  create: (data) => api.post('/api/qualifications/records', data),
  update: (id, data) => api.put(`/api/qualifications/records/${id}`, data),
  delete: (id) => api.delete(`/api/qualifications/records/${id}`),
  stats: (params) => api.get('/api/qualifications/records/stats', { params: cleanParams(params) }),
  uploadCertificate: (recordId, file, { issue_date, expiry_date } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (issue_date) formData.append('issue_date', issue_date);
    if (expiry_date) formData.append('expiry_date', expiry_date);
    return api.post(`/api/qualifications/records/${recordId}/certificates`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadCertificate: (certificateId) => api.get(`/api/qualifications/certificates/${certificateId}/download`, { responseType: 'blob' }),
  deleteCertificate: (certificateId) => api.delete(`/api/qualifications/certificates/${certificateId}`),
};

// ============== Regulatório: Vigilância Sanitária API ==============
export const sanitarySurveillanceApi = {
  list: (params) => api.get('/api/regulatory-control/sanitary-surveillance/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/regulatory-control/sanitary-surveillance/${id}`),
  create: (data) => api.post('/api/regulatory-control/sanitary-surveillance/', data),
  update: (id, data) => api.put(`/api/regulatory-control/sanitary-surveillance/${id}`, data),
  delete: (id) => api.delete(`/api/regulatory-control/sanitary-surveillance/${id}`),
  stats: () => api.get('/api/regulatory-control/sanitary-surveillance/stats'),
};

// ============== Regulatório: Laudo de Potabilidade da Água API ==============
export const waterPotabilityReportApi = {
  list: (params) => api.get('/api/regulatory-control/water-potability-report/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/regulatory-control/water-potability-report/${id}`),
  create: (data) => api.post('/api/regulatory-control/water-potability-report/', data),
  update: (id, data) => api.put(`/api/regulatory-control/water-potability-report/${id}`, data),
  delete: (id) => api.delete(`/api/regulatory-control/water-potability-report/${id}`),
  stats: () => api.get('/api/regulatory-control/water-potability-report/stats'),
};

// ============== Regulatório: Cliente Qualificado API (Documentos para Qualificação de Clientes) ==============
export const clientQualificationApi = {
  list: (params) => api.get('/api/client-qualification/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/client-qualification/${id}`),
  create: (data) => api.post('/api/client-qualification/', data),
  update: (id, data) => api.put(`/api/client-qualification/${id}`, data),
  delete: (id) => api.delete(`/api/client-qualification/${id}`),
  stats: () => api.get('/api/client-qualification/stats'),
  // Workflow
  sendForm: (email) => api.post('/api/client-qualification/send-form', { email }),
  resendForm: (id) => api.post(`/api/client-qualification/${id}/resend-form`),
  startAnalysis: (id) => api.post(`/api/client-qualification/${id}/start-analysis`),
  approve: (id) => api.post(`/api/client-qualification/${id}/approve`),
  reject: (id, observation) => api.post(`/api/client-qualification/${id}/reject`, { observation }),
  // Attachments (admin view)
  listAttachments: (id) => api.get(`/api/client-qualification/${id}/attachments`),
  downloadAttachment: (id) => api.get(`/api/client-qualification/attachments/${id}/download`, { responseType: 'blob' }),
};

// ============== Regulatório: Classificação de Cliente — link público ==============
export const publicClientQualificationApi = {
  get: (token) => api.get(`/api/public/client-qualification/${token}`),
  submit: (token, data) => api.post(`/api/public/client-qualification/${token}/submit`, data),
  uploadAttachment: (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/public/client-qualification/${token}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (token, id) => api.delete(`/api/public/client-qualification/${token}/attachments/${id}`),
  downloadAttachment: (token, id) =>
    api.get(`/api/public/client-qualification/${token}/attachments/${id}/download`, { responseType: 'blob' }),
};

// ============== Regulatório: Análise e Crítica dos Requisitos do Cliente ==============
export const clientRequirementAnalysisApi = {
  list: (params) => api.get('/api/client-requirement-analysis/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/client-requirement-analysis/${id}`),
  create: (data) => api.post('/api/client-requirement-analysis/', data),
  update: (id, data) => api.put(`/api/client-requirement-analysis/${id}`, data),
  delete: (id) => api.delete(`/api/client-requirement-analysis/${id}`),
  stats: () => api.get('/api/client-requirement-analysis/stats'),
  // Workflow
  sendForm: (email) => api.post('/api/client-requirement-analysis/send-form', { email }),
  resendForm: (id) => api.post(`/api/client-requirement-analysis/${id}/resend-form`),
  startAnalysis: (id) => api.post(`/api/client-requirement-analysis/${id}/start-analysis`),
  approve: (id) => api.post(`/api/client-requirement-analysis/${id}/approve`),
  reject: (id, observation) => api.post(`/api/client-requirement-analysis/${id}/reject`, { observation }),
  // Attachments (admin view)
  listAttachments: (id) => api.get(`/api/client-requirement-analysis/${id}/attachments`),
  downloadAttachment: (id) => api.get(`/api/client-requirement-analysis/attachments/${id}/download`, { responseType: 'blob' }),
};

export const publicClientRequirementAnalysisApi = {
  get: (token) => api.get(`/api/public/client-requirement-analysis/${token}`),
  submit: (token, data) => api.post(`/api/public/client-requirement-analysis/${token}/submit`, data),
  uploadAttachment: (token, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/public/client-requirement-analysis/${token}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (token, id) => api.delete(`/api/public/client-requirement-analysis/${token}/attachments/${id}`),
  downloadAttachment: (token, id) =>
    api.get(`/api/public/client-requirement-analysis/${token}/attachments/${id}/download`, { responseType: 'blob' }),
};

// ============== Segurança do Trabalho API (F-007, F-010, F-026, F-029, F-065) ==============
// F-026 (checklist) é atendido pelo módulo de Auto Inspeção (inspectionsApi), não por esta API.
export const laborSafetyApi = {
  formCodes: () => api.get('/api/labor-safety/form-codes'),
  list: (formCode, status) => api.get('/api/labor-safety/', { params: { form_code: formCode, status: status || undefined } }),
  get: (id) => api.get(`/api/labor-safety/${id}`),
  create: (formCode, data, { nextDate, responsibleId } = {}) => api.post('/api/labor-safety/', { form_code: formCode, data, next_date: nextDate || undefined, responsible_id: responsibleId || undefined }),
  update: (id, data, { nextDate, responsibleId } = {}) => api.put(`/api/labor-safety/${id}`, { data, next_date: nextDate || undefined, responsible_id: responsibleId || undefined }),
  delete: (id) => api.delete(`/api/labor-safety/${id}`),
  stats: (formCode) => api.get('/api/labor-safety/stats', { params: { form_code: formCode } }),
  download: (id) => api.get(`/api/labor-safety/${id}/download`, { responseType: 'blob' }),
  downloadAll: (formCode) => api.get(`/api/labor-safety/download-all/${formCode}`, { responseType: 'blob' }),
};

// ============== Inspections API ==============
export const inspectionsApi = {
  // Checklists
  listChecklists: (params) => api.get('/api/inspections/checklists/', { params: cleanParams(params) }),
  getChecklist: (id) => api.get(`/api/inspections/checklists/${id}`),
  getChecklistItems: (id) => api.get(`/api/inspections/checklists/${id}/items`),
  createChecklist: (data) => api.post('/api/inspections/checklists/', data),
  // Inspections
  list: (params) => api.get('/api/inspections/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/inspections/${id}`),
  create: (data) => api.post('/api/inspections/', data),
  update: (id, data) => api.put(`/api/inspections/${id}`, data),
  delete: (id) => api.delete(`/api/inspections/${id}`),
  stats: () => api.get('/api/inspections/stats'),
  // Item results (usado pela aba do formulário BPA)
  getResults: (inspectionId) => api.get(`/api/inspections/${inspectionId}/results`),
  saveResults: (inspectionId, items) => api.put(`/api/inspections/${inspectionId}/results`, items),
  downloadReportPdf: (inspectionId) => api.get(`/api/inspections/${inspectionId}/report/pdf`, { responseType: 'blob' }),
};

// ============== SST API ==============
export const sstApi = {
  // Occurrences
  listOccurrences: (params) => api.get('/api/sst/occurrences/', { params: cleanParams(params) }),
  getOccurrence: (id) => api.get(`/api/sst/occurrences/${id}`),
  createOccurrence: (data) => api.post('/api/sst/occurrences/', data),
  updateOccurrence: (id, data) => api.put(`/api/sst/occurrences/${id}`, data),
  deleteOccurrence: (id) => api.delete(`/api/sst/occurrences/${id}`),
  approveOccurrence: (id) => api.post(`/api/sst/occurrences/${id}/approve`),
  occurrenceStats: () => api.get('/api/sst/occurrences/stats'),
  // EPIs
  listEpis: (params) => api.get('/api/sst/epis/', { params: cleanParams(params) }),
  createEpi: (data) => api.post('/api/sst/epis/', data),
  updateEpi: (id, data) => api.put(`/api/sst/epis/${id}`, data),
  deleteEpi: (id) => api.delete(`/api/sst/epis/${id}`),
  epiStats: () => api.get('/api/sst/epis/stats'),
  // EPI Deliveries
  listDeliveries: (epiId) => api.get(`/api/sst/epis/${epiId}/deliveries`),
  createDelivery: (data) => api.post('/api/sst/epis/deliver', data),
  // Indicators
  listIndicators: (params) => api.get('/api/sst/indicators/', { params }),
  createIndicator: (data) => api.post('/api/sst/indicators/', data),
  calculateIndicatorPreview: (params) => api.get('/api/sst/indicators/calculate', { params }),
  // Work Permits (PTA / PTE)
  listWorkPermits: (params) => api.get('/api/sst/work-permits/', { params: cleanParams(params) }),
  getWorkPermit: (id) => api.get(`/api/sst/work-permits/${id}`),
  createWorkPermit: (data) => api.post('/api/sst/work-permits/', data),
  updateWorkPermit: (id, data) => api.put(`/api/sst/work-permits/${id}`, data),
  deleteWorkPermit: (id) => api.delete(`/api/sst/work-permits/${id}`),
  approveWorkPermit: (id) => api.post(`/api/sst/work-permits/${id}/approve`),
};

// ============== Trainings API ==============
export const trainingsApi = {
  list: (params) => api.get('/api/trainings/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/trainings/${id}`),
  create: (data) => api.post('/api/trainings/', data),
  update: (id, data) => api.put(`/api/trainings/${id}`, data),
  delete: (id) => api.delete(`/api/trainings/${id}`),
  stats: () => api.get('/api/trainings/stats'),
  calendar: (params) => api.get('/api/trainings/calendar', { params }),
  // Participants
  listParticipants: (trainingId) => api.get(`/api/trainings/${trainingId}/participants`),
  addParticipant: (trainingId, data) => api.post(`/api/trainings/${trainingId}/participants`, data),
  updateParticipant: (participantId, data) => api.put(`/api/trainings/participants/${participantId}`, data),
  deleteParticipant: (participantId) => api.delete(`/api/trainings/participants/${participantId}`),
  // Expiring
  listExpiring: (days) => api.get(`/api/trainings/expiring/`, { params: { days } }),
  // Competencies
  listCompetencies: (params) => api.get('/api/trainings/competencies/', { params }),
  createCompetency: (data) => api.post('/api/trainings/competencies/', data),
};

// ============== Complaints API ==============
export const complaintsApi = {
  list: (params) => api.get('/api/complaints/', { params: cleanParams(params) }),
  get: (id) => api.get(`/api/complaints/${id}`),
  create: (data) => api.post('/api/complaints/', data),
  update: (id, data) => api.put(`/api/complaints/${id}`, data),
  delete: (id) => api.delete(`/api/complaints/${id}`),
  stats: () => api.get('/api/complaints/stats'),
  // Interactions
  listInteractions: (complaintId) => api.get(`/api/complaints/${complaintId}/interactions`),
  addInteraction: (complaintId, data) => api.post(`/api/complaints/${complaintId}/interactions`, data),
};

// ============== Calendar API ==============
export const calendarApi = {
  getEvents: (params) => api.get('/api/calendar/events', { params }),
  getSummary: () => api.get('/api/calendar/summary'),
};

// ============== Notifications API ==============
export const notificationsApi = {
  // Email Settings
  getEmailSettings: () => api.get('/api/notifications/email-settings'),
  saveEmailSettings: (data) => api.post('/api/notifications/email-settings', data),
  testEmail: (data) => api.post('/api/notifications/email-settings/test', data),
  
  // Notification Configs
  listConfigs: () => api.get('/api/notifications/configs'),
  updateConfig: (id, data) => api.put(`/api/notifications/configs/${id}`, data),
  
  // Notification Logs
  listLogs: (params) => api.get('/api/notifications/logs', { params }),
  getStats: () => api.get('/api/notifications/stats'),
  
  // Send
  sendManual: (data) => api.post('/api/notifications/send-manual', data),
  processNotifications: () => api.post('/api/notifications/process-pending'),
};

// ============== Scheduler API ==============
export const accessControlApi = {
  get: () => api.get('/api/access-control'),
  updateSettings: (data) => api.put('/api/access-control/settings', data),
  addIp: (data) => api.post('/api/access-control/ips', data),
  updateIp: (id, data) => api.put(`/api/access-control/ips/${id}`, data),
  deleteIp: (id) => api.delete(`/api/access-control/ips/${id}`),
};

export const schedulerApi = {
  listConfigs: () => api.get('/api/scheduler/configs'),
  updateConfig: (id, data) => api.put(`/api/scheduler/configs/${id}`, data),
  listLogs: (params) => api.get('/api/scheduler/logs', { params }),
  getStats: () => api.get('/api/scheduler/stats'),
  runTask: (taskName) => api.post(`/api/scheduler/run/${taskName}`),
};

// ============== Export API ==============
export const exportApi = {
  // Returns blob for download
  download: async (module, format, params = {}) => {
    const response = await api.get(`/api/export/${module}/${format}`, {
      params,
      responseType: 'blob'
    });
    return response;
  },
};

// ============== Sectors API (Cadastros) ==============
export const sectorsApi = {
  list: (params) => api.get('/api/sectors', { params }),
  listActive: () => api.get('/api/sectors/active'),
  getTree: () => api.get('/api/sectors/tree'),
  get: (id) => api.get(`/api/sectors/${id}`),
  getUsers: (id) => api.get(`/api/sectors/${id}/users`),
  create: (data) => api.post('/api/sectors', data),
  update: (id, data) => api.put(`/api/sectors/${id}`, data),
  delete: (id) => api.delete(`/api/sectors/${id}`),
  assignUser: (sectorId, userId) => api.post(`/api/sectors/${sectorId}/users/${userId}`),
  removeUser: (sectorId, userId) => api.delete(`/api/sectors/${sectorId}/users/${userId}`),
};

// ============== Processes API (Cadastros) ==============
export const processesApi = {
  list: (params) => api.get('/api/processes', { params }),
  listActive: () => api.get('/api/processes/active'),
  get: (id) => api.get(`/api/processes/${id}`),
  create: (data) => api.post('/api/processes', data),
  update: (id, data) => api.put(`/api/processes/${id}`, data),
  delete: (id) => api.delete(`/api/processes/${id}`),
};

// ============== Types API (Cadastros) ==============
export const typesApi = {
  list: (params) => api.get('/api/types', { params }),
  listActive: (params) => api.get('/api/types/active', { params }),
  getCategories: () => api.get('/api/types/categories'),
  get: (id) => api.get(`/api/types/${id}`),
  create: (data) => api.post('/api/types', data),
  update: (id, data) => api.put(`/api/types/${id}`, data),
  delete: (id) => api.delete(`/api/types/${id}`),
  reactivate: (id) => api.patch(`/api/types/${id}/reactivate`),
};

// ============== Audit Logs API (F4-03) ==============
export const auditLogsApi = {
  list: (params) => api.get('/api/audit-logs', { params: cleanParams(params) }),
  count: () => api.get('/api/audit-logs/count'),
};

// ============== Periodic Review API (F4-09) ==============
export const periodicReviewApi = {
  list: () => api.get('/api/periodic-reviews'),
  create: (data) => api.post('/api/periodic-reviews', data),
  update: (id, data) => api.put(`/api/periodic-reviews/${id}`, data),
  delete: (id) => api.delete(`/api/periodic-reviews/${id}`),
};

// ============== Electronic Signature (F4-06) ==============
export const signatureApi = {
  verify: (password) => api.post('/api/auth/verify-password', { password }),
};

// ============== Auth Context ==============
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const AuthContext = createContext(null);

// ============== Public API (sem autenticação) ==============
export const publicAuditApi = {
  get: (token) => api.get(`/api/public/audits/${token}`),
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, setUser, token, loading, login, logout, isAuthenticated: !!token }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
